// How far the sponsorship AI may go, its writing tone, and the show
// profile it pitches from.

import { useState, type FormEvent } from 'react';
import {
  AUTONOMY_MODES,
  TONES,
  useAutonomy,
  useSaveAutonomy,
  useSaveProfile,
  useSponsorProfile,
  type SponsorProfile,
} from '../../lib/sponsors';
import { errorInfo } from '../../lib/api';
import { Button } from '../ui/Button';
import { SelectField, TextArea, TextField } from '../ui/Field';
import { ErrorMessage, Loading } from '../ui/Feedback';
import { useToast } from '../ui/Toast';

export function SponsorSettings() {
  const autonomy = useAutonomy();
  const profile = useSponsorProfile();
  if (autonomy.isLoading || profile.isLoading) return <Loading />;
  if (autonomy.isError) return <ErrorMessage error={autonomy.error} onRetry={() => autonomy.refetch()} />;
  return (
    <div className="grid max-w-4xl gap-6 lg:grid-cols-2">
      <AutonomyForm
        level={autonomy.data?.autonomyLevel ?? 'approve_each'}
        tone={autonomy.data?.preferredTone ?? 'professional'}
        limit={autonomy.data?.dailyOutreachLimit ?? 10}
      />
      {profile.data && <ProfileForm profile={profile.data} />}
    </div>
  );
}

function AutonomyForm({ level, tone, limit }: { level: string; tone: string; limit: number }) {
  const save = useSaveAutonomy();
  const toast = useToast();
  // A stored mode we no longer offer (see AUTONOMY_MODES) shows as the safest one.
  const known = AUTONOMY_MODES.some((m) => m.value === level);
  const [mode, setMode] = useState(known ? level : 'approve_each');
  const [preferredTone, setTone] = useState(tone);
  const [daily, setDaily] = useState(String(limit));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const dailyOutreachLimit = Math.max(0, Number.parseInt(daily, 10) || 0);
    save.mutate(
      { autonomyLevel: mode, preferredTone, dailyOutreachLimit },
      {
        onSuccess: () => toast.show('Settings saved', 'success'),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-border p-5">
      <h2 className="text-lg font-semibold">How the AI works</h2>
      <fieldset className="space-y-2">
        <legend className="sr-only">What the AI may do</legend>
        {AUTONOMY_MODES.map((m) => (
          <label
            key={m.value}
            className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${mode === m.value ? 'border-accent bg-accent-soft' : 'border-border'}`}
          >
            <input type="radio" name="mode" value={m.value} checked={mode === m.value} onChange={() => setMode(m.value)} className="mt-1" />
            <span>
              <span className="block text-sm font-medium">{m.label}</span>
              <span className="block text-xs text-muted">{m.description}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <SelectField
        label="Writing tone"
        value={preferredTone}
        onChange={(e) => setTone(e.target.value)}
        options={TONES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
      />
      <TextField
        label="Most pitches per day"
        type="number"
        min={0}
        value={daily}
        onChange={(e) => setDaily(e.target.value)}
        hint="Keeps outreach at a pace that doesn’t look like spam."
      />
      <Button type="submit" loading={save.isPending}>
        Save
      </Button>
    </form>
  );
}

function ProfileForm({ profile }: { profile: SponsorProfile }) {
  const save = useSaveProfile();
  const toast = useToast();
  const [podcastName, setName] = useState(profile.podcastName);
  const [podcastDescription, setDescription] = useState(profile.podcastDescription ?? '');
  const [niche, setNiche] = useState(profile.niche ?? '');
  const [audience, setAudience] = useState(profile.audienceSize !== null ? String(profile.audienceSize) : '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const audienceSize = Number.parseInt(audience.replace(/[^\d]/g, ''), 10);
    save.mutate(
      {
        podcastName: podcastName.trim(),
        podcastDescription: podcastDescription.trim(),
        niche: niche.trim(),
        ...(Number.isFinite(audienceSize) ? { audienceSize } : {}),
      },
      {
        onSuccess: () => toast.show('Profile saved', 'success'),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-border p-5">
      <h2 className="text-lg font-semibold">What the AI pitches</h2>
      <TextField label="Podcast name" value={podcastName} onChange={(e) => setName(e.target.value)} required maxLength={200} />
      <TextArea label="What it’s about" value={podcastDescription} onChange={(e) => setDescription(e.target.value)} />
      <TextField label="Niche" value={niche} onChange={(e) => setNiche(e.target.value)} maxLength={100} />
      <TextField label="Downloads per episode" inputMode="numeric" value={audience} onChange={(e) => setAudience(e.target.value)} />
      <Button type="submit" loading={save.isPending} disabled={!podcastName.trim()}>
        Save profile
      </Button>
    </form>
  );
}
